/**
 * PrintFileIssuesList - Tab content for the Print Files issues tab
 *
 * Shows reported print file/design issues with product thumbnail, SKU, product
 * name, variant info, reason badge, and a resolve action. file_missing issues
 * get a "Upload file" primary action that opens the self-heal flow; other
 * print-file issues keep the manual resolve.
 *
 * Self-heal additions:
 *  - per-row selection (Set<string>) + select-all + shift-click range,
 *  - a selection toolbar that opens the bulk self-heal modal,
 *  - live updates via SSE (usePrintFileEvents) so healed/new issues refresh.
 */
import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileWarning, CheckCircle, Clock, ImageOff, UploadCloud, X, RefreshCw, Loader2, FilePlus, ExternalLink, FolderSearch, ChevronDown, Info, Square } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { PageLoader } from '@/components/loading/PageLoader';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { SegmentedControl } from '@/components/ui/segmented-control';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { getUserMessage } from '@/lib/trpc-error-utils';
import { useOrganization } from '@/contexts/OrganizationContext';
import { ClientOrgChip } from '@/components/ClientOrgChip';
import { useClientOrgLabels } from '@/hooks/useClientOrgLabels';
import { platenGroupLabel } from '@/lib/platen-size-range';
import { PlatenVariantUpdatesLink } from './PlatenVariantUpdatesLink';
import { issueProductRoute, usePlatenVariantRoute } from '../hooks/usePlatenVariantRoute';
import { useRole } from '@/hooks/use-role';
import { useClientOrgFilter, ALL_CLIENTS } from '@/features/inventory/hooks/useClientOrgFilter';
import { ClientFilterDropdown } from '@/features/inventory/components/ClientFilterDropdown';
import { getShopifyAdminOrderUrl, getShopifyAdminVariantUrl } from '@/lib/shopify-urls';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePrintFileIssues, useUnresolveIssue, useIssueCounts } from '../hooks/useProductionIssues';
import { usePrintFileIssueRecheck } from '../hooks/usePrintFileIssueRecheck';
import { useSelfHealCustomNow } from '../hooks/useSelfHealCustomNow';
import { useBulkSelfHeal } from '../hooks/useBulkSelfHeal';
import { isSelfHealEligible, selfHealIdsForSelection } from '../bulkSelfHeal';
import { usePrintFileEvents } from '@/hooks/usePrintFileEvents';
import {
  NO_ARTWORK_DETECTED_METADATA_KEY,
  PLATEN_VARIANT_FILE_NEEDED_METADATA_KEY,
  type PlatenVariantFileNeededMarker,
  type PrintFileOpenCategory,
} from '@threadheads/shared';
import { awaitingBridgeHoldReason, healFailReasonKey, isNoBridgeWorkReason } from '../bridgeFetchState';
import { PRINT_FILE_REASON_CONFIG } from '../types';
import type { ProductionIssue } from '../types';
import { IssueFilterChips } from './IssueFilterChips';
import { IssueSearchInput } from './IssueSearchInput';
import { ResolveDialog } from './ResolveDialog';
import { BridgeFetchBadge } from './BridgeFetchBadge';
import {
  PrintFileIssueBulkUploadModal,
  type BulkUploadIssueSummary,
} from './PrintFileIssueBulkUploadModal';
import type { SelfHealKind } from '../hooks/usePrintFileIssueBulkUpload';

const PAGE_SIZE = 25;

/**
 * Resolved-view segment — mirrors the print-station fixed-feed tabs (#1615).
 * 'manual' | 'self_heal' filter the RESOLVED rows by the server-stamped
 * `resolvedCategory` ratchet; 'all' shows every row. OPEN rows are NEVER
 * filtered — the segmentation is about resolved history only.
 */
type ResolvedSegment = 'manual' | 'self_heal' | 'all';

/**
 * OPEN-view filter chips: Needs manual (DEFAULT — the operator-actionable rows
 * come first) | Auto-fetching (automation still working; watch, don't race it)
 * | Waiting for bridge (BLU-663: held until a daemon that can rip BOTH sides
 * checks in — nothing is fetching yet) | All. The first three map to the
 * server-derived `deriveOpenCategory` filter and PARTITION the open total;
 * 'all' sends no category. Without the awaiting-bridge chip those rows would
 * be invisible on every chip but All, because the server stopped counting
 * them as needs_manual. Deliberately not persisted: every visit starts on
 * Needs manual (mirrors the resolved segment's decree).
 */
type OpenCategoryFilter = PrintFileOpenCategory | 'all';

interface PrintFileIssuesListProps {
  locationId: string;
}

/** Is this issue a missing-file issue (self-heal eligible)? */
function isFileMissing(issue: ProductionIssue): boolean {
  return issue.metadata?.reason === 'file_missing';
}

/** Default print-file kind for an issue, derived from its metadata. */
function deriveKind(issue: ProductionIssue): SelfHealKind {
  // The self-heal worker stamps metadata.kind ('regular' | 'ondemand').
  return issue.metadata?.kind === 'ondemand' ? 'ondemand' : 'regular';
}

export function PrintFileIssuesList({ locationId }: PrintFileIssuesListProps) {
  const { t } = useTranslation('production');
  const [showResolved, setShowResolved] = useState(false);
  // Manual is ALWAYS the default segment (Brett's decree) — operator-relevant
  // history first; Self-Healing is the audit trail of silent auto-heals.
  // Deliberately not persisted: every visit starts on Manual.
  const [resolvedSegment, setResolvedSegment] = useState<ResolvedSegment>('manual');
  // Needs manual is ALWAYS the default chip — the operator-actionable slice
  // first; Auto-fetching is the watch-list. Not persisted, like the segment.
  const [openCategory, setOpenCategory] = useState<OpenCategoryFilter>('needs_manual');
  // Search feeds a SERVER query (order # / SKU / filename / product name), so
  // debounce it — same reason useOosFilters debounces its search.
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 250);
  const searchActive = debouncedSearch.trim().length > 0;
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // "Select all N": every matching self-heal-eligible id across pages, from
  // the server (same filters as the list). Null = the selection is page-only.
  // `truncated` = the server hit its row bound, so this is the first N only.
  const [allMatching, setAllMatching] = useState<{ ids: string[]; truncated: boolean } | null>(null);
  const allMatchingIds = allMatching?.ids ?? null;
  const [bulkOpen, setBulkOpen] = useState(false);
  const [isRechecking, setIsRechecking] = useState(false);
  const lastClickedIndexRef = useRef<number | null>(null);
  // BLU-1287: the issue whose resolve dialog is open (at most one: the dialog
  // is modal). Its card stays mounted until the dialog closes, even when a
  // refetch drops the issue (a save resolved it, or moved it to another
  // category), so the dialog's next step (variant set-up, upload) never
  // vanishes. Every other card follows the latest data as before.
  const [dialogIssue, setDialogIssue] = useState<ProductionIssue | null>(null);

  const { organization } = useOrganization();
  // BLU-817: client-org filter, narrowing the board to one 3PL client at this
  // shared location. `rows: []` — the option list comes from the connected-
  // client roster (`orgIdToName`), not from the loaded page: this list is
  // server-paged, so deriving options from rows would be circular once the
  // rows are already narrowed by this same selection. Mirrors
  // ProductionTrackerPage's client filter (closest analogue: production
  // workspace, location-scoped, server-paged).
  const clientFilter = useClientOrgFilter<{ organization_id?: string | null }>({
    rows: [],
    ownOrgId: organization?.id,
    ownOrgName: organization?.name,
    locationId,
    resetWhenAbsent: false,
  });
  const clientOrgFilter = clientFilter.clientOrgFilter;
  const setClientOrgFilter = clientFilter.setClientOrgFilter;
  const isClientFiltered = clientOrgFilter !== ALL_CLIENTS;
  /** Sent to the server (list + counts, together — see the counts hook
   *  below). Undefined = all clients. */
  const clientOrgFilterParam = isClientFiltered ? clientOrgFilter : undefined;
  const clientFilterOptions = useMemo(
    () => Array.from(clientFilter.orgIdToName.keys()),
    [clientFilter.orgIdToName]
  );

  // A LOCATION change must clear the selection: the chosen client may not be
  // connected at the new floor.
  useEffect(() => {
    setClientOrgFilter(ALL_CLIENTS);
  }, [locationId, setClientOrgFilter]);

  const utils = trpc.useUtils();
  const { recheckMany } = usePrintFileIssueRecheck();

  // Live updates: refresh the list when the worker heals or raises an issue
  // at this location.
  usePrintFileEvents(locationId);

  // Server-side pagination. The OPEN view drives total pages from the exact
  // open count (getIssueCounts — uncapped COUNT(*)); the resolved view (no exact
  // count available) uses full-page detection. Fetching one PAGE_SIZE page at a
  // time replaces the old "fetch ≤50, slice locally" path that silently truncated
  // locations with >50 issues. Selection is page-scoped (cleared on navigation),
  // so a single fetched page is all we need in memory.
  //
  // BLU-817: the client filter is threaded into counts too (not just list) —
  // counts and the page must narrow TOGETHER, or the pager quotes a total
  // computed over a different population than the rows on screen.
  const { data: counts } = useIssueCounts(locationId, {
    clientOrganizationId: clientOrgFilterParam,
  });
  const openTotal = counts?.print_file_issues ?? 0;
  // Chip-aware exact total for the OPEN view. Null → fall back to full-page
  // detection: while SEARCHING the server can't return a filtered total
  // through the bare-array list contract, and a stale pre-feature counts
  // cache may lack the per-category fields.
  const openCategoryTotal =
    openCategory === 'auto_fetching'
      ? counts?.print_file_auto_fetching
      : openCategory === 'awaiting_bridge'
        ? counts?.print_file_awaiting_bridge
        : openCategory === 'needs_manual'
          ? counts?.print_file_needs_manual
          : counts?.print_file_issues;
  const knownTotal =
    !showResolved && !searchActive && typeof openCategoryTotal === 'number'
      ? openCategoryTotal
      : null;
  const totalPages = knownTotal !== null ? Math.max(1, Math.ceil(knownTotal / PAGE_SIZE)) : null;
  const safePage = totalPages !== null ? Math.min(page, totalPages) : page;

  const { data: issues, isLoading } = usePrintFileIssues(locationId, {
    status: showResolved ? undefined : 'OPEN',
    // 'all' sends no category filter; the chips only exist on the OPEN view.
    category: !showResolved && openCategory !== 'all' ? openCategory : undefined,
    search: searchActive ? debouncedSearch.trim() : undefined,
    clientOrganizationId: clientOrgFilterParam,
    limit: PAGE_SIZE,
    offset: (safePage - 1) * PAGE_SIZE,
  });

  const unresolveMutation = useUnresolveIssue();

  // The fetched page IS the current page (no client-side slicing).
  const paginatedIssues = useMemo(() => issues ?? [], [issues]);
  const hasPrevPage = safePage > 1;
  const hasNextPage =
    totalPages !== null
      ? safePage < totalPages
      : paginatedIssues.length === PAGE_SIZE; // a full page → there may be more

  // Resolved-view Manual/Self-Healing partition, mirroring the print-station
  // fixed-feed tabs (#1615) via the server-stamped `resolvedCategory` ratchet.
  // Counts are PAGE-SCOPED (the resolved view has no exact total — see the
  // full-page pagination note above), matching the station panel's
  // loaded-items counts. Missing/stale category → Manual, the safe side:
  // never hide something an operator may have known about.
  const resolvedOnPage = useMemo(
    () => paginatedIssues.filter((i) => i.status === 'RESOLVED'),
    [paginatedIssues]
  );
  const selfHealCount = useMemo(
    () => resolvedOnPage.filter((i) => i.resolvedCategory === 'self_heal').length,
    [resolvedOnPage]
  );
  const manualCount = resolvedOnPage.length - selfHealCount;
  // OPEN (and legacy ROUTED) rows are NEVER filtered — the segmentation is
  // about resolved history only. Pagination stays keyed off the unfiltered
  // page (Prev/Next walk the underlying list; the chips segment what shows).
  const visibleIssues = useMemo(() => {
    if (!showResolved || resolvedSegment === 'all') return paginatedIssues;
    return paginatedIssues.filter(
      (i) =>
        i.status !== 'RESOLVED' ||
        (i.resolvedCategory === 'self_heal') === (resolvedSegment === 'self_heal')
    );
  }, [paginatedIssues, showResolved, resolvedSegment]);

  // The open dialog's issue when the latest data no longer has it: rendered
  // after the visible cards (behind the modal) from its snapshot at open time.
  const pinnedIssue =
    dialogIssue && !visibleIssues.some((i) => i.id === dialogIssue.id) ? dialogIssue : null;
  const cardIssues = pinnedIssue ? [...visibleIssues, pinnedIssue] : visibleIssues;

  // Selectable = OPEN, file_missing issues among the VISIBLE rows (self-heal
  // only applies to a missing file; resolved rows can't be re-uploaded).
  // Keyed off visibleIssues, NOT paginatedIssues: the resolved-segment chips
  // hide rows client-side, and the shift-click range + select-all must index
  // over what the operator can actually see — an index built over hidden rows
  // silently selects them (pre-existing trap, now reachable via the filters).
  // A platen variant size is never uploaded from an issue (its file comes
  // from Product updates), so it never joins a bulk upload either.
  const selectableOnPage = useMemo(
    () =>
      visibleIssues.filter(
        (i) => i.status === 'OPEN' && isFileMissing(i) && i.platenVariantFileNeeded !== true,
      ),
    [visibleIssues]
  );
  const selectableIds = useMemo(
    () => selectableOnPage.map((i) => i.id),
    [selectableOnPage]
  );

  const handleToggleRow = useCallback(
    (index: number, id: string, shiftKey: boolean) => {
      setSelected((prev) => {
        const next = new Set(prev);
        const last = lastClickedIndexRef.current;
        if (shiftKey && last !== null) {
          // Shift-click range: select every selectable row between anchor and
          // this row inclusive.
          const [lo, hi] = last < index ? [last, index] : [index, last];
          for (let i = lo; i <= hi; i++) {
            const row = selectableOnPage[i];
            if (row) next.add(row.id);
          }
        } else if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
      lastClickedIndexRef.current = index;
    },
    [selectableOnPage]
  );

  const allSelectableSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelectableSelected = selectableIds.some((id) => selected.has(id));

  const handleSelectAll = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelectableSelected) {
        selectableIds.forEach((id) => next.delete(id));
      } else {
        selectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
    lastClickedIndexRef.current = null;
  }, [allSelectableSelected, selectableIds]);

  const clearSelection = useCallback(() => {
    setSelected(new Set());
    setAllMatching(null);
    lastClickedIndexRef.current = null;
  }, []);

  // A LOCATION change also drops the row selection and any "Select all N":
  // those ids belong to the old floor and must never be healed from the new one.
  useEffect(() => {
    clearSelection();
  }, [locationId, clearSelection]);

  // ---- Bulk Self-Heal Now ------------------------------------------------
  // Rows on this page that show the per-row Self-Heal Now button.
  const selfHealEligibleOnPage = useMemo(
    () => new Set(selectableOnPage.filter(isSelfHealEligible).map((i) => i.id)),
    [selectableOnPage]
  );
  const selfHealIds = useMemo(
    () => selfHealIdsForSelection(selected, selfHealEligibleOnPage, allMatchingIds),
    [selected, selfHealEligibleOnPage, allMatchingIds]
  );
  const bulkSelfHeal = useBulkSelfHeal();

  // "Select all N" (OPEN view, more pages than this one, whole page already
  // selected): ask the server for every matching eligible id under the SAME
  // filters as the list, never a browser filter of one page. One bounded read,
  // only fetched once the operator has selected the whole page.
  // Any other page counts (Prev or Next): on the last page the matching rows
  // sit on the pages before it.
  const offerSelectAllMatching =
    !showResolved && (hasPrevPage || hasNextPage) && allSelectableSelected && allMatchingIds === null;
  const { data: matchingSelfHeal } = trpc.productionOrders.issues.listSelfHealIds.useQuery(
    {
      location_id: locationId,
      category: openCategory !== 'all' ? openCategory : undefined,
      search: searchActive ? debouncedSearch.trim() : undefined,
      client_organization_id: clientOrgFilterParam,
    },
    { enabled: !!locationId && offerSelectAllMatching, retry: false }
  );
  const moreToSelect =
    offerSelectAllMatching &&
    matchingSelfHeal !== undefined &&
    matchingSelfHeal.ids.some((id) => !selected.has(id));

  // After "Select all N", the label follows unticks: count only the ids from
  // that set that are still selected.
  const allMatchingStillSelected = useMemo(
    () => (allMatching ? allMatching.ids.filter((id) => selected.has(id)).length : 0),
    [allMatching, selected]
  );

  const handleSelectAllMatching = useCallback(() => {
    if (!matchingSelfHeal) return;
    setSelected((prev) => new Set([...prev, ...matchingSelfHeal.ids]));
    setAllMatching(matchingSelfHeal);
  }, [matchingSelfHeal]);

  const handleBulkSelfHeal = useCallback(async () => {
    if (bulkSelfHeal.running || selfHealIds.length === 0) return;
    const tally = await bulkSelfHeal.run(selfHealIds);
    const base = t(
      tally.stopped > 0
        ? 'issues.printFiles.bulkSelfHeal.summaryStopped'
        : 'issues.printFiles.bulkSelfHeal.summary',
      { ...tally }
    );
    // Never started because the server stayed busy: its own sentence, never
    // folded into "still failing" (nothing was tried).
    const busySentence = t('issues.printFiles.bulkSelfHeal.summaryBusy', { count: tally.busy });
    const summary = tally.busy > 0 ? `${base} ${busySentence}` : base;
    if (tally.failed === 0 && tally.healing === 0 && tally.stopped === 0 && tally.busy === 0)
      toast.success(summary);
    else toast.warning(summary);
    clearSelection();
  }, [bulkSelfHeal, selfHealIds, t, clearSelection]);

  // Bulk re-check: re-run the self-heal decision per selected issue (resolve if
  // present on active, heal from backup, else leave open). Summarise + refresh.
  const handleRecheckSelected = useCallback(async () => {
    if (isRechecking) return;
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setIsRechecking(true);
    try {
      const results = await recheckMany(ids);
      const resolved = results.filter((r) => r.status === 'resolved').length;
      const healed = results.filter((r) => r.status === 'healed').length;
      const requeued = results.filter((r) => r.status === 'requeued').length;
      // `healing` = a custom-source heal is actively running but hasn't
      // settled (deadline elapsed / in-flight dedupe / concurrency cap) — its
      // OWN bucket, not folded into stillMissing: it's "still working", not a
      // dead end, mirroring `requeued`'s treatment.
      const healing = results.filter((r) => r.status === 'healing').length;
      const stillMissing = results.filter((r) => r.status === 'still_missing').length;
      // BLU-651 A1: wrong-data issues are refused by the server-side re-check
      // (the file exists but is WRONG — existence proves nothing). Own bucket
      // so the operator learns these need a corrected upload, not a re-check.
      const wrongData = results.filter((r) => r.status === 'wrong_data_needs_upload').length;
      const metadataMismatch = results.filter((r) => r.status === 'metadata_mismatch').length;
      // BLU-855: the corrected metadata is saved locally but Shopify never
      // confirmed it. Its own bucket — folding it into "still missing" would
      // send the operator to upload a file that was never the problem.
      const metadataSyncFailed = results.filter((r) => r.status === 'metadata_sync_failed').length;
      // A platen variant size stays open until its own file is uploaded in
      // Product updates: its own bucket, never "still missing" (an issue
      // upload cannot close it).
      const platenVariant = results.filter((r) => r.status === 'platen_variant_file_needed').length;
      // The platen variant clause only when a row is in that bucket: an org
      // without platen size rules never reads about platen variant files.
      const summaryKey =
        platenVariant > 0
          ? 'issues.printFiles.recheck.bulkSummaryWithPlatenVariant'
          : 'issues.printFiles.recheck.bulkSummary';
      const summary = t(summaryKey, {
        resolved,
        healed,
        requeued,
        healing,
        stillMissing,
        wrongData,
        metadataMismatch,
        metadataSyncFailed,
        platenVariant,
      });
      // Success only when EVERY row reached a resolved/healed state; warning
      // (amber) when anything still needs an upload, a re-queue, or is still
      // actively healing. The summary above already carries the requeued count
      // — no separate info toast (unlike the single-issue ResolveDialog path,
      // which has no summary line to fold it into).
      if (
        stillMissing === 0 &&
        requeued === 0 &&
        healing === 0 &&
        wrongData === 0 &&
        metadataMismatch === 0 &&
        metadataSyncFailed === 0 &&
        platenVariant === 0
      )
        toast.success(summary);
      else toast.warning(summary);
      // Resolved/healed drop off the open list; still-missing/requeued/healing remain.
      // Also refresh the counts so the open total + page count stay accurate,
      // and requeued rows' "Auto-fetching" pill reflects the reset counter.
      utils.productionOrders.issues.list.invalidate();
      utils.productionOrders.issues.getCounts.invalidate();
      clearSelection();
    } catch (error) {
      toast.error(getUserMessage(error));
    } finally {
      setIsRechecking(false);
    }
  }, [isRechecking, selected, recheckMany, t, utils, clearSelection]);

  const selectedSummaries: BulkUploadIssueSummary[] = useMemo(
    () =>
      paginatedIssues
        .filter((i) => selected.has(i.id))
        .map((i) => ({
          issueId: i.id,
          sku: i.blocked_sku,
          orderName: i.order_name ?? null,
          productName:
            i.design_product_name ??
            (i.metadata?.product_name as string) ??
            i.blocked_sku ??
            'Unknown Product',
          defaultKind: deriveKind(i),
        })),
    [paginatedIssues, selected]
  );

  const handleUnresolve = async (issueId: string) => {
    try {
      await unresolveMutation.mutateAsync({ issue_id: issueId });
      toast.success(t('issues.printFiles.reopened'));
    } catch (error) {
      toast.error(getUserMessage(error));
    }
  };

  // While a resolve dialog is open the list renders through a load (the
  // page clamps after a save, a search settles) instead of the loader: the
  // loader would unmount the card holding the dialog, and its remount would
  // reopen the dialog at its first step. With no data the cards are just the
  // pinned one, behind the modal; closing the dialog brings the loader back.
  if (isLoading && !dialogIssue) {
    return <PageLoader variant="production" pool="issues" />;
  }

  const selectedCount = selected.size;

  return (
    <div>
      {/* Search + OPEN-view category chips — ABOVE the selection ternary so
          they survive row selection (everything inside it disappears when a
          row is selected). Search applies to both views; chips are OPEN-only
          (the resolved view keeps its #1618 SegmentedControl). */}
      <div className="mb-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <IssueSearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
              clearSelection();
            }}
            placeholder={t('issues.printFiles.searchPlaceholder')}
            ariaLabel={t('issues.filters.searchAriaLabel')}
            clearLabel={t('issues.filters.clearSearch')}
          />
          {/* BLU-817: client-org filter — only renders at a shared location
              with connected clients (single-tenant operators see no chrome
              change). Not role-gated: `listConnectedClientsAtLocation`'s role
              allowlist already covers every role that can reach this page. */}
          {clientFilter.showClientFilter && (
            <ClientFilterDropdown
              value={clientOrgFilter}
              onChange={(next) => {
                setClientOrgFilter(next);
                setPage(1);
                clearSelection();
              }}
              ownOrgId={organization?.id}
              orgIdToName={clientFilter.orgIdToName}
              distinctOrgsInData={clientFilterOptions}
            />
          )}
          {isClientFiltered && !showResolved && (
            /* The on-screen count is now client-scoped (counts narrowed
               together with the list — see useIssueCounts above), so it must
               never be mistaken for the tab's unfiltered total on the Issues
               page tab strip. Mirrors OosFilters' hasActiveFilters affordance. */
            <span className="text-sm text-muted-foreground">
              {t('issues.filters.matchingCount', { count: openTotal })}
            </span>
          )}
          {searchActive && safePage === 1 && !hasNextPage && (
            /* Live match count — only when the WHOLE match set is on screen
               (page 1, no further pages): the list is server-paginated, so a
               partial page has no honest total to quote. */
            <span className="text-sm text-muted-foreground">
              {t('issues.searchMatchCount', { count: paginatedIssues.length })}
            </span>
          )}
        </div>
        {!showResolved && (
          <IssueFilterChips<OpenCategoryFilter>
            accent="purple"
            ariaLabel={t('issues.printFiles.openFilter.aria')}
            value={openCategory}
            onChange={(value) => {
              setOpenCategory(value);
              setPage(1);
              clearSelection();
            }}
            options={[
              {
                value: 'needs_manual',
                label: t('issues.printFiles.openFilter.needsManual'),
                count: counts?.print_file_needs_manual,
              },
              {
                value: 'auto_fetching',
                label: t('issues.printFiles.openFilter.autoFetching'),
                count: counts?.print_file_auto_fetching,
              },
              {
                value: 'awaiting_bridge',
                label: t('issues.printFiles.openFilter.awaitingBridge'),
                count: counts?.print_file_awaiting_bridge,
              },
              {
                value: 'all',
                label: t('issues.printFiles.openFilter.all'),
                count: openTotal,
              },
            ]}
          />
        )}
      </div>

      {/* Selection toolbar — replaces the controls row when rows are selected */}
      {selectedCount > 0 || bulkSelfHeal.running ? (
        <div
          className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-threadheads-purple/30 bg-threadheads-purple/5 px-3 py-2"
          role="region"
          aria-label={t('issues.printFiles.bulk.toolbarLabel')}
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-threadheads-purple">
              {t('issues.printFiles.bulk.selectedCount', { count: selectedCount })}
            </span>
            {/* Cross-page select: only once the whole page is ticked and the
                server says more matching rows can self-heal. */}
            {moreToSelect && matchingSelfHeal && (
              <Button
                variant="link"
                size="sm"
                className="h-8 px-1 text-sm text-threadheads-purple"
                onClick={handleSelectAllMatching}
                disabled={bulkSelfHeal.running}
              >
                {matchingSelfHeal.truncated
                  ? t('issues.printFiles.bulkSelfHeal.selectFirstMatching', {
                      count: matchingSelfHeal.ids.length,
                    })
                  : t('issues.printFiles.bulkSelfHeal.selectAllMatching', {
                      count: matchingSelfHeal.ids.length,
                    })}
              </Button>
            )}
            {allMatching !== null && (
              <span className="text-sm text-muted-foreground">
                {allMatchingStillSelected === allMatching.ids.length
                  ? t(
                      allMatching.truncated
                        ? 'issues.printFiles.bulkSelfHeal.firstMatchingSelected'
                        : 'issues.printFiles.bulkSelfHeal.allMatchingSelected',
                      { count: allMatching.ids.length }
                    )
                  : t('issues.printFiles.bulkSelfHeal.someMatchingSelected', {
                      count: allMatchingStillSelected,
                    })}
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1 text-xs text-muted-foreground"
              onClick={clearSelection}
              disabled={bulkSelfHeal.running}
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
              {t('issues.printFiles.bulk.clear')}
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {bulkSelfHeal.progress ? (
              <>
                <span role="status" className="text-sm font-medium tabular-nums">
                  <Loader2
                    className="mr-1.5 inline h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                  {t('issues.printFiles.bulkSelfHeal.progress', {
                    done: bulkSelfHeal.progress.done,
                    total: bulkSelfHeal.progress.total,
                  })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5"
                  onClick={bulkSelfHeal.stop}
                  disabled={bulkSelfHeal.stopping}
                >
                  <Square className="h-3.5 w-3.5" aria-hidden="true" />
                  {bulkSelfHeal.stopping
                    ? t('issues.printFiles.bulkSelfHeal.stopping')
                    : t('issues.printFiles.bulkSelfHeal.stop')}
                </Button>
              </>
            ) : (
              selfHealIds.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5"
                  onClick={handleBulkSelfHeal}
                  disabled={isRechecking}
                >
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('issues.printFiles.bulkSelfHeal.button', { count: selfHealIds.length })}
                </Button>
              )
            )}
            {/* Re-check and upload work on the rows on screen. A cross-page
                "Select all N" selection only offers Self-Heal Now. */}
            {allMatchingIds === null && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5"
                  onClick={handleRecheckSelected}
                  disabled={isRechecking || bulkSelfHeal.running}
                >
                  {isRechecking ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  {t('issues.printFiles.recheck.bulkButton')}
                </Button>
                <Button
                  size="sm"
                  className="h-8 gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
                  onClick={() => setBulkOpen(true)}
                  disabled={isRechecking || bulkSelfHeal.running}
                >
                  <UploadCloud className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('issues.printFiles.bulk.uploadSelected', { count: selectedCount })}
                </Button>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                id="show-resolved-print"
                checked={showResolved}
                onCheckedChange={(checked) => {
                  setShowResolved(checked);
                  // Every entry to the resolved view starts on Manual —
                  // nothing persisted, by decree.
                  setResolvedSegment('manual');
                  setPage(1);
                  clearSelection();
                }}
              />
              <Label htmlFor="show-resolved-print" className="text-sm text-muted-foreground">
                {t('issues.printFiles.showResolved')}
              </Label>
            </div>
            {selectableIds.length > 0 && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handleSelectAll}
                      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
                    >
                      <Checkbox
                        checked={
                          allSelectableSelected
                            ? true
                            : someSelectableSelected
                              ? 'indeterminate'
                              : false
                        }
                        aria-label={t('issues.printFiles.bulk.selectAll')}
                        className="pointer-events-none"
                      />
                      {t('issues.printFiles.bulk.selectAll')}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('issues.printFiles.bulk.selectAllTooltip', {
                      count: selectableIds.length,
                    })}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {showResolved ? (
            /* Manual (default) | Self-Healing | All — segments the RESOLVED
               rows on this page by the server-stamped ratchet category; OPEN
               rows always show. Same vocabulary as the print-station fixed
               feed (#1615). Page-scoped counts (no exact resolved total). */
            <SegmentedControl<ResolvedSegment>
              ariaLabel={t('issues.printFiles.resolvedSegment.label')}
              value={resolvedSegment}
              onChange={setResolvedSegment}
              options={[
                {
                  value: 'manual',
                  label: t('issues.printFiles.resolvedSegment.manual'),
                  badge: manualCount,
                },
                {
                  value: 'self_heal',
                  label: t('issues.printFiles.resolvedSegment.selfHeal'),
                  badge: selfHealCount,
                },
                {
                  value: 'all',
                  label: t('issues.printFiles.resolvedSegment.all'),
                  badge: resolvedOnPage.length,
                },
              ]}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('issues.printFiles.issueCount', { count: openTotal })}
            </p>
          )}
        </div>
      )}

      {/* Issue cards */}
      {paginatedIssues.length === 0 && !pinnedIssue ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <FileWarning className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
            {searchActive || (!showResolved && openCategory !== 'all' && openTotal > 0) ? (
              /* A search / category chip is hiding rows that exist — say so
                 instead of implying the location is clean. */
              <>
                <p className="mt-4 text-lg font-medium">
                  {t('issues.printFiles.openFilter.emptyTitle')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('issues.printFiles.openFilter.emptyHint')}
                </p>
              </>
            ) : (
              <>
                <p className="mt-4 text-lg font-medium">{t('issues.printFiles.noIssues')}</p>
                <p className="text-sm text-muted-foreground">
                  {showResolved
                    ? t('issues.printFiles.noneReported')
                    : t('issues.printFiles.noOpen')}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      ) : visibleIssues.length === 0 && !pinnedIssue ? (
        /* The page has rows but the active segment matches none of them —
           point at the All segment rather than implying nothing exists. */
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <FileWarning className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
            <p className="mt-4 text-lg font-medium">
              {t('issues.printFiles.resolvedSegment.emptyTitle')}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('issues.printFiles.resolvedSegment.emptyHint')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {cardIssues.map((issue) => {
            const selectableIndex = selectableOnPage.findIndex((s) => s.id === issue.id);
            return (
              <PrintFileIssueCard
                key={issue.id}
                issue={issue}
                selectable={selectableIndex !== -1}
                selected={selected.has(issue.id)}
                onToggleSelect={(shiftKey) =>
                  handleToggleRow(selectableIndex, issue.id, shiftKey)
                }
                onUnresolve={handleUnresolve}
                isUnresolving={unresolveMutation.isPending}
                resolveDialogOpen={dialogIssue?.id === issue.id}
                // A late close (a save settling after its dialog closed) only
                // clears its own dialog, never another card's open one.
                onResolveDialogOpenChange={(open) =>
                  setDialogIssue((cur) => (open ? issue : cur?.id === issue.id ? null : cur))
                }
              />
            );
          })}
        </div>
      )}

      {/* Pagination (server-side) */}
      {(hasPrevPage || hasNextPage) && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={!hasPrevPage}
            onClick={() => { setPage(Math.max(1, safePage - 1)); clearSelection(); }}
          >
            {t('issues.pagination.previous')}
          </Button>
          <span className="text-sm text-muted-foreground">
            {totalPages !== null
              ? t('issues.pagination.pageOf', { current: safePage, total: totalPages })
              : t('issues.pagination.pageCurrent', { current: safePage })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNextPage}
            onClick={() => { setPage(safePage + 1); clearSelection(); }}
          >
            {t('issues.pagination.next')}
          </Button>
        </div>
      )}

      {/* Bulk self-heal modal */}
      <PrintFileIssueBulkUploadModal
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        issues={selectedSummaries}
        onComplete={clearSelection}
      />
    </div>
  );
}

// ============================================================================
// PrintFileIssueCard
// ============================================================================

interface PrintFileIssueCardProps {
  issue: ProductionIssue;
  selectable: boolean;
  selected: boolean;
  onToggleSelect: (shiftKey: boolean) => void;
  onUnresolve: (issueId: string) => void;
  isUnresolving: boolean;
  /** BLU-1287: the list owns the open state, so it can keep this card
   *  mounted while its dialog is open (see dialogIssue). */
  resolveDialogOpen: boolean;
  onResolveDialogOpenChange: (open: boolean) => void;
}

function PrintFileIssueCard({
  issue,
  selectable,
  selected,
  onToggleSelect,
  onUnresolve,
  isUnresolving,
  resolveDialogOpen,
  onResolveDialogOpenChange,
}: PrintFileIssueCardProps) {
  const { t } = useTranslation('production');
  const { t: tSettings } = useTranslation('settings');
  const { organization } = useOrganization();
  // BLU-3PL: cross-org chip when a 3PL owner views a client's print-file issue.
  const { isCrossOrg, labelFor } = useClientOrgLabels();
  // Preview is a short-TTL signed URL (custom previews) — fall back to the
  // ImageOff placeholder if it 404s/expires, like the print/catcher stations.
  const [imgFailed, setImgFailed] = useState(false);
  const [showDesignsCandidates, setShowDesignsCandidates] = useState(false);
  // Self-Heal Now — immediate server-side custom-source heal (bypasses the
  // bridge queue's poison cap). Per-card mutation instance; the utils
  // invalidation refreshes list + counts on a resolve.
  const { selfHealNow, healingIssueId } = useSelfHealCustomNow();
  const selfHealing = healingIssueId === issue.id;
  const utils = trpc.useUtils();
  const metadata = issue.metadata;
  const reason = (metadata?.reason as string) ?? 'unknown';
  // Server-enriched fields (resolved from the live order line) win over the
  // metadata snapshot. The title NEVER falls back to the SKU — that was the
  // source of the duplicate-SKU look on self-heal issues.
  const productName =
    issue.design_product_name ??
    (metadata?.product_name as string) ??
    t('issues.printFiles.unknownProduct');
  // SKU shown exactly ONCE (prefer the design-bearing shopify_sku).
  const displaySku = issue.design_sku ?? issue.blocked_sku;
  // Product preview pulled from the DB following the print/catcher station rule
  // (signed custom preview → product image). Snapshot metadata is the fallback.
  const previewUrl = issue.preview_image_url ?? (metadata?.image_url as string | null) ?? null;
  const variantTitle = issue.variant_title ?? (metadata?.variant_title as string | null) ?? null;
  const color = issue.color ?? (metadata?.color as string | null) ?? null;
  const size = issue.size ?? (metadata?.size as string | null) ?? null;
  const filename = metadata?.filename as string | null;
  const missingFormats = Array.isArray(metadata?.missing_formats)
    ? [...new Set(metadata.missing_formats.filter((format): format is 'arx4' | 'png' => format === 'arx4' || format === 'png'))]
    : [];
  const backend = metadata?.backend as string | null;
  const metadataMismatch = metadata?.metadata_mismatch as
    | { sku_design_id?: string; lookup_design_id?: string }
    | undefined;
  // BLU-590: why the hands-off designs-folder heal declined for this issue
  // (stamped by the sweep / recheck). metadata is an untyped bag, so narrow here.
  const designsSource = metadata?.designs_source as
    | {
        status?: 'ambiguous' | 'no_match' | 'drive_error' | 'download_failed' | 'resize_failed' | 'write_failed';
        candidates?: string[];
        expected_filenames?: string[];
        folder_source?: 'override' | 'default';
        folder_code?: string;
      }
    | undefined;
  const isResolved = issue.status === 'RESOLVED';
  const fileMissing = reason === 'file_missing';
  // Phase 86: uploaded from Product updates only (server flag from the marker).
  const platenVariantFileNeeded = issue.platenVariantFileNeeded === true && !isResolved;
  // Who uploads it: this admin (link), an admin (ask), or the 3PL client (ask).
  const platenRoute = usePlatenVariantRoute(issue.organization_id, issue.shopify_product_id);
  // BLU-1287: the same who-acts rule for the resolve dialog's variant set-up
  // step, from the labels and role this card already holds (no request).
  const { isAdmin } = useRole();
  const productRoute = issueProductRoute(
    isCrossOrg(issue.organization_id),
    labelFor(issue.organization_id) ?? t('issues.printFiles.platenVariant.theClient'),
    isAdmin(),
  );
  // Feature B: a dual with no print_file_lookups row — actionable via the
  // front/back print-data form (a distinct purple CTA, like fileMissing).
  const printDataNeeded = reason === 'print_data_needed';
  // Phase 60-05: the bootleg-bridge is auto-refetching this missing custom file
  // from source (server derives `bridgeEligible` from a stored line-item artwork
  // URL). Show a neutral status pill and demote the manual upload CTA so an
  // operator doesn't race the bridge with a manual upload — resolve is
  // fail-closed on file *presence*, so whoever lands last wins on *content*.
  // Poisoned: the bridge queue served this issue MAX attempts without a
  // resolve and will never retry it — the calm "Auto-fetching" state would be
  // a lie. Show the failed state + manual actions instead.
  const bridgePoisoned = issue.bridgePoisoned === true && !isResolved;
  // The calm "Auto-fetching" pill renders ONLY while the server still
  // classifies the row as auto-fetching (deriveOpenCategory / openCategory).
  // bridgeEligible alone is not enough: a row whose sweep source-retry
  // exhausted (metadata.source_retry.exhausted) — or whose deferred filed
  // Slack already fired — stays bridge-eligible forever, and the pill used to
  // spin on it indefinitely. That lie is what this gate ends.
  const bridgeAutoFetching =
    issue.bridgeEligible === true &&
    !bridgePoisoned &&
    !isResolved &&
    issue.openCategory === 'auto_fetching';
  // Sweep-side exhaustion: automation gave up on a bridge-eligible row without
  // tripping the bridge-attempt poison cap (source retries exhausted / filed
  // Slack fired). Same operator meaning as bridgePoisoned — automation is
  // done, act manually — so it reuses the poisoned presentation wholesale
  // (failed pill + hint, Self-Heal Now primary, upload demoted to ghost).
  const autoFetchGaveUp =
    issue.bridgeEligible === true &&
    !bridgePoisoned &&
    !isResolved &&
    issue.openCategory === 'needs_manual';
  // BLU-663: the work is QUEUED BEHIND A CAPABILITY, not in flight — no
  // connected daemon can rip front and back for this product yet. The
  // auto-fetch spinner would claim something is happening right now; nothing
  // is, and nothing the operator does makes a bridge appear. Its own explicit
  // pill, amber (waiting), no spinner.
  const awaitingBridge = !isResolved && issue.openCategory === 'awaiting_bridge';
  // BLU-663 shipped EIGHT distinct holds behind that one category, and only one
  // of them ("no bridge can do both sides") matches the old fixed hint. The
  // rest are a storage backend the bridge cannot serve, an unmeasured printer
  // fleet, a transient read that retries itself, a missing artwork side, or
  // simply nothing left to make. So the row names the actual hold and the
  // actual next step; the generic hint is the fallback for an unrecognised
  // code, never the default answer.
  const holdReason = awaitingBridge ? awaitingBridgeHoldReason(issue.bridgeFetchFailReason) : null;
  // Two of the holds are not a wait at all — the bridge has nothing to make.
  // A "waiting for bridge" badge on those would be a lie in the loudest place.
  const noBridgeWork = isNoBridgeWorkReason(holdReason);
  // Self-Heal Now lifecycle persisted by the executor (metadata.custom_source):
  // `failed` = last attempt failed (reason attached). `replicating` /
  // `sync_pending` are HISTORICAL values only — nothing writes them since
  // BLU-826 (a heal verified on the ingest bucket IS healed), and nothing
  // renders them; they stay in the type because old rows still carry them.
  const customSource = metadata?.custom_source as
    | {
        status?: 'replicating' | 'sync_pending' | 'failed' | 'requeued';
        reason?: string;
        at?: string;
      }
    | undefined;
  const healFailed = !isResolved && customSource?.status === 'failed';
  // Pill-area priority: self-heal failed > gave-up/poisoned > auto-fetching.
  const showHealFailedPill = healFailed;
  const showPoisonedPill = (bridgePoisoned || autoFetchGaveUp) && !healFailed;
  const showAwaitingBridgePill = awaitingBridge && !healFailed && !showPoisonedPill;
  const showAutoFetchingPill = bridgeAutoFetching && !healFailed && !showAwaitingBridgePill;
  // BLU-880: a live (non-failed) bridge state on the row upgrades the static
  // auto-fetching pill to the live step. `failed` is the poisoned pill's job;
  // `awaiting_bridge` is not a step in the run (see showAwaitingBridgePill).
  const liveBridgeState =
    issue.bridgeFetchState &&
    issue.bridgeFetchState !== 'failed' &&
    issue.bridgeFetchState !== 'awaiting_bridge'
      ? issue.bridgeFetchState
      : null;
  // Any bridge/heal state demotes the manual-upload CTA to a quiet ghost —
  // Self-Heal Now is the single loud element instead.
  const uploadDemoted = bridgeAutoFetching || bridgePoisoned || autoFetchGaveUp || healFailed;
  // What the CTA actually does for this row. Only a row whose fix is "put
  // files on the cloud" may say "Upload print files"; a metadata row opens a
  // design-data form and a wrong-artwork row opens the correction flow.
  const resolveCtaLabel = fileMissing
    ? t('issues.printFiles.upload.uploadArtwork')
    : printDataNeeded
      ? t('issues.printFiles.upload.enterDesignData')
      : t('issues.resolve');
  const handleSelfHealNow = async () => {
    try {
      const result = await selfHealNow(issue.id);
      if (result.status === 'healed') {
        toast.success(t('issues.printFiles.selfHealHealed'));
        utils.productionOrders.issues.list.invalidate();
        utils.productionOrders.issues.getCounts.invalidate();
      } else if (result.status === 'requeued') {
        toast.info(t('issues.printFiles.selfHealRequeued'));
        // The attempt counter was reset server-side — bridgePoisoned is now
        // false; refetch so the red "failed" pill flips back to auto-fetching.
        utils.productionOrders.issues.list.invalidate();
      } else if (result.status === 'healing') {
        // BLU-1309: still running after 20 s; the bell tells them how it ends.
        toast.info(t('issues.printFiles.selfHealStillWorking'));
        utils.productionOrders.issues.list.invalidate();
      } else {
        // F26: the same reason map as the paste dialog.
        toast.error(t(healFailReasonKey(result.reason)));
        // The executor persisted the failed state + reason — refetch so the
        // "Self-heal failed" pill renders (and survives refresh).
        utils.productionOrders.issues.list.invalidate();
      }
    } catch (err) {
      toast.error(getUserMessage(err));
    }
  };

  // Shopify admin deep-links (null when ids/domain are missing → render plain text).
  const orderUrl = getShopifyAdminOrderUrl(organization?.shopify_domain, issue.shopify_order_id);
  const variantUrl = getShopifyAdminVariantUrl(
    organization?.shopify_domain,
    issue.shopify_product_id,
    issue.shopify_variant_id,
  );

  const reasonConfig = PRINT_FILE_REASON_CONFIG[reason] ?? {
    label: reason.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    color: 'text-gray-700 bg-gray-100 border-gray-200',
  };
  // Restored look: a missing print file wears the red "Missing Artwork" badge.
  // Reuse the canonical missing-artwork token rather than hand-rolling the class.
  const missingArtworkColor =
    PRINT_FILE_REASON_CONFIG.missing_artwork?.color ?? 'text-red-700 bg-red-100 border-red-200';
  // F9: when the bridge is auto-fetching this row, soften the reason badge from
  // red (loudest semantic colour = "act now") to amber ("attention / in
  // progress"), so the alarm doesn't fight the calm grey "Auto-fetching" pill
  // and pull the operator into the manual-upload race the demotion prevents.
  // Reuse the canonical amber token (low_resolution / print_data_needed).
  const bridgeFetchingColor =
    PRINT_FILE_REASON_CONFIG.print_data_needed?.color ?? 'text-amber-700 bg-amber-100 border-amber-200';

  // Short operator guide, mirroring the Cancelled card's "why" panel. Keep
  // the diagnosis and next action separate so the floor can scan it quickly.
  const issueGuide = (() => {
    // BLU-817: a line born without artwork shares the `missing_artwork` reason (that
    // reason owns the recovery UI), so it would otherwise fall through to the
    // fileMissing guidance — "check the file name below, upload the file" — for a row
    // that has no filename and no file to upload. The station now tells the printer
    // "tell your coordinator", which makes this the coordinator's only instruction.
    if (metadata?.[NO_ARTWORK_DETECTED_METADATA_KEY] === true) {
      return {
        title: t('issues.printFiles.guide.noArtworkAttached.title'),
        action: t('issues.printFiles.guide.noArtworkAttached.action'),
      };
    }
    // Phase 86 (D-22): this size falls under a platen size rule and has no
    // platen variant file, so no automation will ever fill it. The generic
    // "check the file name, upload the file" guide would be wrong twice: the
    // file name is right, and the file must come from product sync. A floor
    // whose print storage cannot serve a platen variant file at all gets its
    // own guide (the backend flags it on the marker).
    const platenMarker = metadata?.[PLATEN_VARIANT_FILE_NEEDED_METADATA_KEY] as
      | Partial<PlatenVariantFileNeededMarker>
      | undefined;
    if (platenMarker && typeof platenMarker === 'object') {
      if (platenMarker.unsupported_backend === true) {
        return {
          title: t('issues.printFiles.guide.platenVariantUnsupported.title'),
          action: t('issues.printFiles.guide.platenVariantUnsupported.action'),
        };
      }
      const group =
        platenMarker.platen && platenMarker.platen_variant
          ? platenGroupLabel(t, { platen: platenMarker.platen, platenVariant: platenMarker.platen_variant })
          : t('issues.printFiles.platenVariant.genericGroup');
      return {
        title: t('issues.printFiles.guide.platenVariantNeeded.title'),
        action:
          platenRoute.kind === 'askClient'
            ? t('issues.printFiles.guide.platenVariantNeeded.actionAskClient', {
                group,
                client: platenRoute.clientName,
              })
            : platenRoute.kind === 'askAdmin'
              ? t('issues.printFiles.guide.platenVariantNeeded.actionAskAdmin', { group })
              : t('issues.printFiles.guide.platenVariantNeeded.action', { group }),
      };
    }
    switch (reason) {
      case 'metadata_mismatch':
        return {
          title: t('issues.printFiles.guide.metadataMismatch.title', {
            skuDesign: metadataMismatch?.sku_design_id ?? '?',
            fileDesign: metadataMismatch?.lookup_design_id ?? '?',
          }),
          action: t('issues.printFiles.guide.metadataMismatch.action'),
        };
      case 'file_missing':
      case 'missing_artwork':
        return {
          title: t('issues.printFiles.guide.fileMissing.title'),
          action: showAutoFetchingPill
            ? t('issues.printFiles.guide.fileMissing.automaticAction')
            : t('issues.printFiles.guide.fileMissing.action'),
        };
      case 'print_data_needed':
        return {
          title: t('issues.printFiles.guide.printDataNeeded.title'),
          action: t('issues.printFiles.guide.printDataNeeded.action'),
        };
      case 'wrong_design':
        return {
          title: t('issues.printFiles.guide.wrongDesign.title'),
          action: t('issues.printFiles.guide.wrongDesign.action'),
        };
      case 'wrong_position':
        return {
          title: t('issues.printFiles.guide.wrongPosition.title'),
          action: t('issues.printFiles.guide.wrongPosition.action'),
        };
      case 'color_mismatch':
        return {
          title: t('issues.printFiles.guide.colorMismatch.title'),
          action: t('issues.printFiles.guide.colorMismatch.action'),
        };
      case 'low_resolution':
        return {
          title: t('issues.printFiles.guide.lowResolution.title'),
          action: t('issues.printFiles.guide.lowResolution.action'),
        };
      case 'supply_mapping_invalid':
        return {
          title: t('issues.printFiles.guide.supplyMappingInvalid.title'),
          action: t('issues.printFiles.guide.supplyMappingInvalid.action'),
        };
      default:
        return {
          title: t('issues.printFiles.guide.other.title'),
          action: t('issues.printFiles.guide.other.action'),
        };
    }
  })();

  return (
    <Card className={selected ? 'ring-1 ring-threadheads-purple/40' : isResolved ? 'opacity-60' : undefined}>
      <CardContent className="py-4">
        <div className="flex items-start gap-3">
          {/* Selection checkbox (self-heal eligible rows only) */}
          {selectable && (
            <div
              className="flex h-16 shrink-0 items-center"
              // Capture shiftKey from the underlying click before the Checkbox
              // fires its change handler.
              onClick={(e) => {
                e.stopPropagation();
                onToggleSelect(e.shiftKey);
              }}
            >
              <Checkbox
                checked={selected}
                aria-label={t('issues.printFiles.bulk.selectRow', {
                  name: productName,
                })}
                className="pointer-events-none"
              />
            </div>
          )}

          {/* Thumbnail — live product preview from the DB (custom signed preview
              → product image), mirroring the print/catcher stations. */}
          <div className="w-16 h-16 rounded-md border bg-muted flex items-center justify-center shrink-0 overflow-hidden">
            {previewUrl && !imgFailed ? (
              <img
                src={previewUrl}
                alt=""
                loading="lazy"
                onError={() => setImgFailed(true)}
                className="w-full h-full object-cover"
              />
            ) : (
              <ImageOff className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
            )}
          </div>

          {/* Main content */}
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-base leading-tight truncate">{productName}</p>
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  {isCrossOrg(issue.organization_id) && (
                    <ClientOrgChip label={labelFor(issue.organization_id)} />
                  )}
                  {displaySku &&
                    (variantUrl ? (
                      <a
                        href={variantUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={t('issues.printFiles.viewVariant')}
                        className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-threadheads-purple/50"
                      >
                        <Badge
                          variant="outline"
                          className="gap-1 text-xs font-mono transition-colors hover:bg-muted"
                        >
                          {displaySku}
                          <ExternalLink className="h-2.5 w-2.5 opacity-60" aria-hidden="true" />
                        </Badge>
                      </a>
                    ) : (
                      <Badge variant="outline" className="text-xs font-mono">
                        {displaySku}
                      </Badge>
                    ))}
                  {size && (
                    <Badge variant="secondary" className="text-xs">
                      {size}
                    </Badge>
                  )}
                  {color && (
                    <Badge variant="secondary" className="text-xs">
                      {color}
                    </Badge>
                  )}
                  {variantTitle && !size && !color && (
                    <span className="text-xs text-muted-foreground">{variantTitle}</span>
                  )}
                </div>
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                {formatDistanceToNow(new Date(issue.created_at), { addSuffix: true })}
              </span>
            </div>

            {/* Reason badge (red "Missing Artwork" when the file is missing) +
                (when the bridge is self-healing) a neutral "Auto-fetching" pill */}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Badge
                variant="outline"
                className={`text-xs border ${
                  fileMissing
                    ? bridgeAutoFetching
                      ? bridgeFetchingColor
                      : missingArtworkColor
                    : reasonConfig.color
                }`}
              >
                {fileMissing ? t('issues.printFiles.fileMissingBadge') : reasonConfig.label}
              </Badge>
              {showHealFailedPill && (
                // The last Self-Heal Now attempt failed — persisted with its
                // reason so the state survives refresh. Red = act now.
                <Badge
                  variant="outline"
                  role="status"
                  className="gap-1 text-xs border text-red-700 bg-red-50 border-red-200"
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                  {t('issues.printFiles.selfHealFailedPill')}
                </Badge>
              )}
              {showAutoFetchingPill && liveBridgeState && (
                // BLU-880: the row carries a live bridge state — show WHERE the
                // bridge is (same vocabulary as the dialog's stepper) instead
                // of the static "auto-fetching" line.
                <BridgeFetchBadge state={liveBridgeState} />
              )}
              {showAutoFetchingPill && !liveBridgeState && (
                // Neutral quiet-grey pill (DESIGN.md §13.1): no accent, no per-row
                // tint. `muted` variant = bg-muted text-muted-foreground; ring (not
                // border) keeps the pill silhouette. role="status" announces the
                // active auto-fetch to screen readers as the row state changes.
                <Badge variant="muted" role="status" className="gap-1 ring-1 ring-border">
                  <RefreshCw
                    className="h-3 w-3 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                  {t('issues.printFiles.autoFetching')}
                </Badge>
              )}
              {showAwaitingBridgePill && (
                // Amber = waiting (PRODUCTION_DESIGN §4.1), Clock not a
                // spinner: nothing is fetching, the job is held for a daemon
                // that can produce BOTH sides.
                <Badge
                  variant="outline"
                  role="status"
                  className="gap-1 border text-xs text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-800"
                >
                  <Clock className="h-3 w-3" aria-hidden="true" />
                  {t(
                    noBridgeWork
                      ? 'issues.printFiles.awaitingBridgeNoWorkPill'
                      : 'issues.printFiles.awaitingBridgePill',
                  )}
                </Badge>
              )}
              {showPoisonedPill && (
                // The bridge gave up (attempt cap) — an honest failed pill in
                // place of the perpetual "Auto-fetching" spinner. Red = act now;
                // Self-Heal Now below is the retry.
                <Badge
                  variant="outline"
                  role="status"
                  className="gap-1 text-xs border text-red-700 bg-red-50 border-red-200"
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                  {t('issues.printFiles.autoHealFailed')}
                </Badge>
              )}
            </div>

            {/* Static escape hatch — the bridge auto-fetch has no durable
                attempt timestamp (BLU-527), so we can't time-bound a "delayed"
                state without risking a mislabel. Instead, quietly point the
                operator at the always-reachable manual upload CTA below. Muted
                text keeps the single-loud-element invariant intact. */}
            {showAutoFetchingPill && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                {t('issues.printFiles.takingLonger')}
              </p>
            )}
            {showAwaitingBridgePill && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                {holdReason
                  ? t(`issues.printFiles.awaitingBridgeReason.${holdReason}`)
                  : t('issues.printFiles.awaitingBridgeHint')}
              </p>
            )}
            {showPoisonedPill && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                {t('issues.printFiles.autoHealFailedHint')}
              </p>
            )}
            {showHealFailedPill && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                {customSource?.reason === 'no_source_url'
                  ? t('issues.printFiles.selfHealFailedNoSource')
                  : customSource?.reason === 'download_failed'
                    ? t('issues.printFiles.failedReasonNetwork')
                    : t('issues.printFiles.failedReasonGenericHint')}
              </p>
            )}

            {/* Plain-language diagnosis + next step, matching the cancelled
                garment card's compact "why" panel. */}
            {!isResolved && (
              <div className="mt-2 rounded-md border bg-muted/50 px-2.5 py-1.5">
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t('issues.printFiles.guide.heading')}
                </p>
                <p className="mt-0.5 text-xs font-medium">{issueGuide.title}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{issueGuide.action}</p>
              </div>
            )}

            {/* Expected file — labeled, prominent block */}
            {(fileMissing || reason === 'metadata_mismatch') && filename && (
              <div className="mt-2 rounded-md border bg-muted/50 px-2.5 py-1.5">
                <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t('issues.printFiles.expectedFile')}
                </p>
                <p className="mt-0.5 font-mono text-xs break-all">{filename}</p>
                {!isResolved && missingFormats.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {missingFormats.map((format) => (
                      <Badge key={format} variant="outline" className="border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
                        {t('issues.printFiles.missingFormat', { format: format.toUpperCase() })}
                      </Badge>
                    ))}
                  </div>
                )}
                {backend && (
                  <p className="text-[11px] text-muted-foreground">
                    {t('issues.printFiles.onBackend', { backend })}
                  </p>
                )}
              </div>
            )}

            {/* BLU-590: designs-folder heal declined. For ACTIONABLE statuses
                (ambiguous / no_match) the operator must fix the folder (rename /
                add a PNG) and re-check — amber attention tone. System errors
                (drive/download/resize/write) are transient and self-retry —
                neutral/informational tone, no false call-to-action. Suppressed
                while the bridge is auto-fetching (that path owns the row) and
                once resolved. */}
            {!isResolved && !bridgeAutoFetching && designsSource?.status && (() => {
              const actionable =
                designsSource.status === 'ambiguous' || designsSource.status === 'no_match';
              const box = actionable
                ? 'border-amber-200 bg-amber-50'
                : 'border-border bg-muted/50';
              const iconColor = actionable ? 'text-amber-600' : 'text-muted-foreground';
              const headText = actionable ? 'text-amber-800' : 'text-muted-foreground';
              const Icon = actionable ? FolderSearch : Info;
              return (
                <div className={`mt-2 rounded-md border px-2.5 py-1.5 ${box}`}>
                  <div className="flex items-start gap-1.5">
                    <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${iconColor}`} aria-hidden="true" />
                    <div className="min-w-0">
                      <p className={`text-xs font-medium ${headText}`}>
                        {designsSource.status === 'ambiguous'
                          ? t('issues.printFiles.designsSource.ambiguous', {
                              count: designsSource.candidates?.length ?? 0,
                            })
                          : designsSource.status === 'no_match'
                            ? t('issues.printFiles.designsSource.noMatch')
                            : t('issues.printFiles.designsSource.systemError')}
                      </p>
                      {designsSource.folder_source && (
                        <p className="text-xs text-muted-foreground">
                          {designsSource.folder_source === 'override' && designsSource.folder_code
                            ? tSettings('production.designsSource.searchedOverride', { code: designsSource.folder_code })
                            : designsSource.folder_source === 'override'
                              ? tSettings('production.designsSource.searchedOverrideUnknown')
                              : tSettings('production.designsSource.searchedDefault')}
                        </p>
                      )}
                      {designsSource.candidates && designsSource.candidates.length > 0 && (
                        <>
                          <button
                            type="button"
                            className="mt-0.5 inline-flex items-center gap-0.5 rounded-sm text-[11px] text-amber-700 underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-threadheads-purple/50"
                            onClick={() => setShowDesignsCandidates((s) => !s)}
                            aria-expanded={showDesignsCandidates}
                            aria-controls={`designs-candidates-${issue.id}`}
                          >
                            {showDesignsCandidates
                              ? t('issues.printFiles.designsSource.hideCandidates')
                              : t('issues.printFiles.designsSource.showCandidates')}
                            <ChevronDown
                              className={`h-3 w-3 transition-transform ${showDesignsCandidates ? 'rotate-180' : ''}`}
                              aria-hidden="true"
                            />
                          </button>
                          {showDesignsCandidates && (
                            <ul
                              id={`designs-candidates-${issue.id}`}
                              className="mt-1 space-y-0.5 font-mono text-[11px] text-amber-800"
                            >
                              {designsSource.candidates.map((name, i) => (
                                <li key={`${name}-${i}`} className="break-all">
                                  {name}
                                </li>
                              ))}
                            </ul>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Notes */}
            {issue.notes && (
              <p className="mt-1.5 text-xs text-muted-foreground italic line-clamp-2">
                &ldquo;{issue.notes}&rdquo;
              </p>
            )}

            {/* Order link + actions */}
            <div className="mt-2 flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                {issue.order_name &&
                  (orderUrl ? (
                    <a
                      href={orderUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={t('issues.printFiles.viewOrder')}
                      className="inline-flex min-w-0 items-center gap-0.5 rounded-sm text-xs font-medium text-threadheads-purple hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-threadheads-purple/50"
                    >
                      <span className="truncate">{issue.order_name}</span>
                      <ExternalLink className="h-3 w-3 shrink-0 opacity-70" aria-hidden="true" />
                    </a>
                  ) : (
                    <span className="truncate text-xs text-muted-foreground">{issue.order_name}</span>
                  ))}
                {isResolved && (
                  <Badge variant="outline" className="text-xs text-green-600 border-green-200 bg-green-50 gap-1">
                    <CheckCircle className="h-3 w-3" aria-hidden="true" />
                    {issue.resolution_method === 'bridge_refetch'
                      ? t('issues.printFiles.autoHealedBadge')
                      : t('issues.printFiles.resolvedBadge')}
                  </Badge>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {/* Self-Heal Now — immediate server-side custom-source heal,
                    bypasses the bridge poison cap. Primary (purple) when the
                    bridge gave up or the last heal failed (it IS the retry);
                    quiet outline while the bridge is auto-fetching. */}
                {!isResolved && fileMissing && issue.bridgeEligible === true && (
                  <Button
                    variant={bridgePoisoned || autoFetchGaveUp || healFailed ? 'default' : 'outline'}
                    size="sm"
                    className={
                      bridgePoisoned || autoFetchGaveUp || healFailed
                        ? 'h-12 gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90'
                        : 'gap-1.5'
                    }
                    disabled={selfHealing}
                    onClick={handleSelfHealNow}
                  >
                    {selfHealing ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    {selfHealing
                      ? t('issues.printFiles.selfHealing')
                      : t('issues.printFiles.selfHealNow')}
                  </Button>
                )}
                {/* Phase 86: a platen variant size's file comes from Product
                    updates, never from the issue upload (which writes a name
                    print does not use). Link straight to this product. */}
                {/* Only an admin of the product's own org can upload there;
                    anyone else reads who to ask in the guide above, with no
                    call to action (see usePlatenVariantRoute). */}
                {!isResolved && platenVariantFileNeeded && platenRoute.kind === 'upload' && (
                  <PlatenVariantUpdatesLink href={platenRoute.href} data-testid="platen-variant-updates-link" />
                )}
                {!isResolved && !platenVariantFileNeeded && (
                  <Button
                    // Bridge-eligible rows (auto-fetching OR poisoned) demote the
                    // manual upload to a quiet ghost CTA — the bridge / Self-Heal
                    // Now is the primary path, upload stays reachable.
                    // none/cloud rows keep the primary purple CTA (operator-only path).
                    variant={
                      uploadDemoted
                        ? 'ghost'
                        : fileMissing || printDataNeeded
                          ? 'default'
                          : 'outline'
                    }
                    size="sm"
                    className={
                      uploadDemoted
                        ? 'h-12 gap-1.5 text-muted-foreground'
                        : fileMissing || printDataNeeded
                          ? 'h-12 gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90'
                          : 'h-12'
                    }
                    onClick={() => onResolveDialogOpenChange(true)}
                  >
                    {fileMissing && !uploadDemoted && (
                      <UploadCloud className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    {printDataNeeded && <FilePlus className="h-3.5 w-3.5" aria-hidden="true" />}
                    {resolveCtaLabel}
                  </Button>
                )}
                {/* Routed issues (resolution_method='routed'; legacy status='ROUTED')
                    moved location — suppress Reopen, the order isn't here anymore. */}
                {isResolved &&
                  issue.status !== 'ROUTED' &&
                  issue.resolution_method !== 'routed' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-muted-foreground"
                    onClick={() => onUnresolve(issue.id)}
                    disabled={isUnresolving}
                  >
                    {t('issues.printFiles.reopen')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>

      <ResolveDialog
        open={resolveDialogOpen}
        onOpenChange={onResolveDialogOpenChange}
        issueId={issue.id}
        locationId={issue.location_id}
        orderName={issue.order_name}
        issueType={issue.issue_type}
        metadata={metadata}
        bridgeFetchState={issue.bridgeFetchState}
        bridgeFetchStateAt={issue.bridgeFetchStateAt}
        bridgeFetchFailReason={issue.bridgeFetchFailReason}
        blockedSku={issue.blocked_sku}
        shopifyProductId={issue.shopify_product_id}
        issueOrganizationId={issue.organization_id}
        productRoute={productRoute}
      />
    </Card>
  );
}
